import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Link,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Create as ContentIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { PanelLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hexToRgba } from '../../user/content/utils/colorUtils';
import LanguageSwitcher from '../LanguageSwitcher';

const drawerWidth = 280;

// Navigation items with translation keys (not translated values)
const navigationItemsConfig = [
  { textKey: 'navigation:sidebar.dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { textKey: 'navigation:sidebar.contentGeneration', icon: <ContentIcon />, path: '/generate' },
  { textKey: 'navigation:sidebar.generationHistory', icon: <HistoryIcon />, path: '/history' },
  { textKey: 'navigation:sidebar.businessProfile', icon: <BusinessIcon />, path: '/business-profiles' },
  { textKey: 'navigation:sidebar.billing', icon: <PaymentIcon />, path: '/billing' },
  { textKey: 'navigation:sidebar.userManagement', icon: <PeopleIcon />, path: '/admin/users', adminOnly: true },
];

interface UserSidebarProps {
  user: any;
  mobileOpen: boolean;
  desktopOpen: boolean;
  onDrawerToggle: () => void;
  onDesktopDrawerToggle: () => void;
  onProfileSettings?: () => void;
  onLogout?: () => void;
}

const UserSidebar: React.FC<UserSidebarProps> = ({ 
  user, 
  mobileOpen, 
  desktopOpen,
  onDrawerToggle,
  onDesktopDrawerToggle,
  onProfileSettings,
  onLogout,
}) => {
  const { t } = useTranslation(['navigation', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileSettings = () => {
    if (onProfileSettings) {
      onProfileSettings();
    } else {
      navigate('/profile');
    }
    handleUserMenuClose();
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    handleUserMenuClose();
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user?.first_name) {
      return user.first_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    if (user?.email) {
      return user.email;
    }
    return 'User';
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        gap: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <Box 
          onClick={() => {
            navigate('/dashboard');
            if (mobileOpen) onDrawerToggle();
          }}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            cursor: 'pointer',
            flexGrow: 1,
            '&:hover': {
              transform: 'scale(1.02)',
              transition: 'transform 0.2s ease-in-out'
            }
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 24, sm: 28, md: 32 },
            height: { xs: 24, sm: 28, md: 32 },
            background: 'transparent',
          }}>
            <Box
              component="img"
              src="/favicon.svg"
              alt="BizContent"
              sx={{
                width: { xs: 24, sm: 28, md: 32 },
                height: { xs: 24, sm: 28, md: 32 },
                objectFit: 'contain',
              }}
            />
          </Box>
          <Typography 
            variant="h5" 
            noWrap 
            component="div" 
            sx={{ 
              fontWeight: 800, 
              color: 'white',
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              letterSpacing: '-0.02em',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              display: { xs: 'block', sm: desktopOpen ? 'block' : 'none' },
            }}
          >
            BizContent
          </Typography>
        </Box>
        <Tooltip title={t('navigation:sidebar.closeSidebar')}>
          <IconButton
            onClick={onDesktopDrawerToggle}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
              },
            }}
          >
            <PanelLeft size={20} />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <List sx={{ flexGrow: 1 }}>
        {navigationItemsConfig
          .filter(item => {
            // For admin users, only show Dashboard and User Management
            if (user?.role === 'admin') {
              return item.path === '/dashboard' || item.adminOnly === true;
            }
            // For regular users, show all items except admin-only items
            return !item.adminOnly;
          })
          .map((item) => (
            <ListItem key={item.textKey} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (mobileOpen) onDrawerToggle();
                }}
                selected={location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path))}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 1,
                  py: 0.5,
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& .MuiListItemIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderLeft: '4px solid white',
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={t(item.textKey)} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      {/* Language Switcher */}
      {desktopOpen && (
        <Box sx={{ px: 2, py: 1 }}>
          <LanguageSwitcher variant="sidebar" />
        </Box>
      )}
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      {/* Support Section */}
      {desktopOpen && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Link
            href="mailto:support@bizcontent.ai"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'rgba(255, 255, 255, 0.8)',
              textDecoration: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                color: 'white',
                textDecoration: 'underline',
              },
            }}
          >
            <EmailIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {t('common:support.contactSupport')}
            </Typography>
          </Link>
        </Box>
      )}
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      {/* User Section */}
      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={handleUserMenuOpen}
          sx={{
            borderRadius: 1,
            py: 1,
            px: 1.5,
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40, 
              bgcolor: 'white',
              color: '#6C63FF',
              fontWeight: 600,
              mr: 1.5,
              border: '2px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            {getUserInitials()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, display: desktopOpen ? 'block' : 'none' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: 'white',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getUserDisplayName()}
            </Typography>
            {user?.email && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {user.email}
              </Typography>
            )}
          </Box>
        </ListItemButton>
      </Box>
      
      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 0,
            ml: 1,
            minWidth: 200,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              bottom: 25,
              left: -5,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem onClick={handleProfileSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('navigation:userMenu.profileSettings')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('navigation:userMenu.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );

  // Collapsed sidebar with icons only
  const collapsedDrawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo/Open button at top */}
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Tooltip title={t('navigation:sidebar.openSidebar')} placement="right">
          <IconButton
            onClick={onDesktopDrawerToggle}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
              },
            }}
          >
            <PanelLeft size={20} />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      
      {/* Navigation icons */}
      <List sx={{ flexGrow: 1, px: 0.5, py: 1 }}>
        {navigationItemsConfig
          .filter(item => {
            // For admin users, only show Dashboard and User Management
            if (user?.role === 'admin') {
              return item.path === '/dashboard' || item.adminOnly === true;
            }
            // For regular users, show all items except admin-only items
            return !item.adminOnly;
          })
          .map((item) => {
            const isSelected = location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <Tooltip key={item.textKey} title={t(item.textKey)} placement="right">
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    selected={isSelected}
                    sx={{
                      minHeight: 48,
                      justifyContent: 'center',
                      borderRadius: 1,
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: isSelected ? 'white' : 'rgba(255, 255, 255, 0.7)' }}>
                      {item.icon}
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}
      </List>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      
      {/* User avatar in collapsed state */}
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
        <Tooltip title={getUserDisplayName()} placement="right">
          <IconButton
            onClick={handleUserMenuOpen}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: 'white',
                color: '#6C63FF',
                fontWeight: 600,
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              {getUserInitials()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: desktopOpen ? drawerWidth : 64 }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            background: 'linear-gradient(180deg, #8b9cff 0%, #6C63FF 50%, #5a52ee 100%)',
            color: 'white',
          },
        }}
      >
        {drawer}
      </Drawer>
      {/* Desktop drawer */}
      <Drawer
        variant="persistent"
        open={true}
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: desktopOpen ? drawerWidth : 64,
            transition: 'width 0.3s ease-in-out',
            overflowX: 'hidden',
            background: 'linear-gradient(180deg, #8b9cff 0%, #6C63FF 50%, #5a52ee 100%)',
            color: 'white',
          },
        }}
      >
        {desktopOpen ? drawer : collapsedDrawer}
      </Drawer>
    </Box>
  );
};

export default UserSidebar;

